<!--
  Sites — every job site with its all-time P&L, and where an admin edits one.

  Two pairs of dates sit side by side on purpose: Start–End is what somebody
  typed on the site, First–Last is what the daily reports actually say. Days
  worked counts distinct report dates, so margin per day is a figure the
  reports earn rather than one a calendar assumes.

  Editing opens SiteFields inline — the same fields the quality area edits.
  Delete only appears for a site nothing has been filed under, and asks
  inline: no modals anywhere in this app.

  Look: the quality area's site list with money in it — a navy hero over cards
  with a safety-orange rail. The figures used to be a table, which on a phone
  was a sideways scroll; a card lets them wrap instead.
-->
<script lang="ts">
  import { api, isApiError } from '../../lib/api';
  import { money } from '../../lib/money';
  import { linkHandler, location, routePath, type PnlScopeQuery } from '../../lib/router';
  import { notify, notifyError } from '../../lib/store';
  import type { AdminSiteRow, SaveSiteRequest, SiteStatus } from '../../lib/types';
  import SiteFields from '../quality/SiteFields.svelte';
  import AdminHero from './AdminHero.svelte';
  import { readScope } from './scope';
  import './admin-look.css';

  let sites = $state<AdminSiteRow[]>([]);
  let loading = $state(true);

  /** The site whose Edit form is open, and the values it is editing. */
  let editing = $state<number | null>(null);
  let draft = $state<SaveSiteRequest>({});
  let saving = $state(false);
  let error = $state<string | null>(null);
  let fieldErrors = $state<Record<string, string>>({});
  /** The site whose Delete is awaiting its inline Yes/No. */
  let confirmDelete = $state<number | null>(null);
  /** The site whose Save is awaiting its inline rename confirmation. */
  let confirmRename = $state<number | null>(null);

  /** The scope a breakdown link carries, so Sites -> a site -> Back keeps the day. */
  const scope = $derived(readScope($location.query));

  /**
   * These figures are all-time, but a breakdown is always one day or one
   * month, so a row opens the month of its last report — the last month
   * anything happened — rather than today, which for a finished site would
   * open an empty page.
   */
  function scopeFor(site: AdminSiteRow): PnlScopeQuery {
    return site.last_report_date ? { month: site.last_report_date.slice(0, 7) } : scope;
  }

  async function load() {
    loading = true;
    try {
      sites = (await api.adminSites()).sites;
    } catch (cause) {
      notifyError(cause, 'Could not load the sites');
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void load();
  });

  function openEdit(site: AdminSiteRow) {
    editing = site.id;
    confirmDelete = null;
    confirmRename = null;
    error = null;
    fieldErrors = {};
    draft = {
      name: site.name, street: site.street, city: site.city, province: site.province,
      postal_code: site.postal_code, status: site.status, start_date: site.start_date,
      end_date: site.end_date, notes: site.notes
    };
  }

  function closeEdit() {
    editing = null;
    confirmRename = null;
    error = null;
    fieldErrors = {};
  }

  /** Every form the rename cascade would rewrite the job name on. */
  function linkedFormCount(site: AdminSiteRow): number {
    return site.jha_count + site.field_test_count + site.equipment_count + site.daily_report_count;
  }

  /** Save directly, unless this Save would rename a site with linked forms — then ask first. */
  function requestSave(site: AdminSiteRow) {
    const newName = (draft.name ?? '').trim();
    const renaming = newName !== '' && newName !== site.name;
    if (renaming && linkedFormCount(site) > 0) {
      confirmRename = site.id;
      return;
    }
    void save(site.id);
  }

  async function save(siteId: number) {
    if (saving) return;
    saving = true;
    error = null;
    fieldErrors = {};
    try {
      await api.updateSite(siteId, draft);
      notify({ tone: 'ok', title: 'Site saved' });
      closeEdit();
      await load();
    } catch (cause) {
      if (isApiError(cause)) {
        error = cause.message;
        fieldErrors = cause.fields ?? {};
      } else {
        error = 'Could not save. Check the connection and try again.';
      }
    } finally {
      saving = false;
    }
  }

  /**
   * A site is FINISHED rather than deleted in normal use, so the toggle is
   * here in the row and not in SiteFields — the fields are shared with the
   * quality area, which owns its own copy of this button.
   */
  async function setStatus(siteId: number, next: SiteStatus) {
    try {
      await api.updateSite(siteId, { status: next });
      notify({ tone: 'ok', title: next === 'finished' ? 'Site marked finished' : 'Site reopened' });
      await load();
    } catch (cause) {
      notifyError(cause, 'Could not change the site status');
    }
  }

  async function remove(siteId: number) {
    try {
      await api.deleteSite(siteId);
      notify({ tone: 'ok', title: 'Site deleted' });
      confirmDelete = null;
      if (editing === siteId) closeEdit();
      await load();
    } catch (cause) {
      notifyError(cause, 'Could not delete that site');
    }
  }

  /** A site nothing has been filed under can go; anything else is finished, not deleted. */
  function isEmpty(site: AdminSiteRow): boolean {
    return (
      site.jha_count === 0 &&
      site.field_test_count === 0 &&
      site.equipment_count === 0 &&
      site.daily_report_count === 0 &&
      site.contract_report_count === 0
    );
  }

  function span(from: string, to: string): string {
    if (!from && !to) return '—';
    return `${from || '—'} – ${to || '—'}`;
  }

  const daysWorked = $derived(sites.reduce((n, s) => n + s.days_worked, 0));
  const totalMargin = $derived(sites.reduce((n, s) => n + s.margin, 0));
  const unpricedTotal = $derived(sites.reduce((n, s) => n + s.unpriced_count, 0));

  /* The headline margin carries the unpriced count for the same reason a row
     does: a figure that silently omits lines is worse than no figure. */
  const stats = $derived([
    { value: String(sites.length), label: 'sites' },
    { value: String(daysWorked), label: 'days worked' },
    {
      value: money(totalMargin),
      label: 'margin, all time',
      tone: totalMargin < 0 ? ('bad' as const) : ('plain' as const)
    },
    ...(unpricedTotal > 0
      ? [{ value: String(unpricedTotal), label: 'lines unpriced', tone: 'bad' as const }]
      : [])
  ]);
</script>

<AdminHero kicker={'Admin · Performance'} title="Sites" {stats}>
  {#snippet links()}
    <a class="a-chip" href={routePath.admin(scope)} onclick={linkHandler}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 12H5m7-7-7 7 7 7" /></svg>
      Dashboard
    </a>
  {/snippet}
</AdminHero>

<p class="note">
  All time, over every daily report. Days worked counts the dates reports were
  filed on, not the days between the start and end dates.
</p>

{#if loading && sites.length === 0}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if sites.length === 0}
  <div class="empty">
    <strong>No sites yet</strong>
    <span class="meta">A site shows up here as soon as one exists in the quality area.</span>
  </div>
{:else}
  <ul class="a-list">
    {#each sites as site, i (site.id)}
      <li style="--i: {i}">
        <div class="a-card site">
          <span class="a-card__rail" class:a-card__rail--muted={site.status !== 'active'} aria-hidden="true"></span>
          <div class="site__body">
            <a class="site__name" href={routePath.adminProject(site.name, scopeFor(site), site.id)} onclick={linkHandler}>
              <span class="site__nametext">{site.name}</span>
              <svg class="a-card__chev" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
            </a>

            <p class="site__dates">
              <span class="pill {site.status === 'active' ? 'pill--ok' : 'pill--pending'}">{site.status}</span>
              <span><small>start – end</small> <span class="a-num">{span(site.start_date, site.end_date)}</span></span>
              <span><small>first – last report</small> <span class="a-num">{span(site.first_report_date ?? '', site.last_report_date ?? '')}</span></span>
            </p>

            <dl class="figs">
              <div class="fig"><dt>Days</dt><dd class="a-num">{site.days_worked}</dd></div>
              <div class="fig">
                <dt>Revenue</dt>
                <dd class="a-num">
                  {money(site.revenue)}
                  <!-- A site whose revenue is entirely contract charges would
                       otherwise read here as ordinary concrete revenue. -->
                  {#if site.contract_revenue > 0}
                    <span class="fig__note">{money(site.contract_revenue)} contract</span>
                  {/if}
                </dd>
              </div>
              <div class="fig"><dt>Cost</dt><dd class="a-num">{money(site.cost)}</dd></div>
              <div class="fig">
                <dt>Margin</dt>
                <dd class="a-num" class:is-neg={site.margin < 0}>
                  {money(site.margin)}
                  <!-- A margin is never shown without what it leaves out. -->
                  {#if site.unpriced_count > 0}
                    <span class="pill pill--warn" title="Lines that could not be priced are not in these figures">
                      {site.unpriced_count} unpriced
                    </span>
                  {/if}
                </dd>
              </div>
              <div class="fig">
                <dt>Margin / day</dt>
                <dd class="a-num" class:is-neg={(site.margin_per_day ?? 0) < 0}>{money(site.margin_per_day)}</dd>
              </div>
            </dl>

            <div class="acts">
              <button class="btn btn--sm" type="button" onclick={() => (editing === site.id ? closeEdit() : openEdit(site))}>
                {editing === site.id ? 'Close' : 'Edit'}
              </button>
              {#if site.status === 'active'}
                <button class="btn btn--sm" type="button" onclick={() => void setStatus(site.id, 'finished')}>Mark finished</button>
              {:else}
                <button class="btn btn--sm" type="button" onclick={() => void setStatus(site.id, 'active')}>Reopen</button>
              {/if}
              {#if isEmpty(site) && confirmDelete !== site.id}
                <button class="btn btn--sm btn--ghost" type="button" onclick={() => (confirmDelete = site.id)}>Delete</button>
              {/if}
            </div>

            {#if isEmpty(site) && confirmDelete === site.id}
              <div class="ask ask--danger">
                <p>Delete this site? Nothing has been filed under it.</p>
                <div class="ask__btns">
                  <button class="btn btn--sm btn--danger" type="button" onclick={() => void remove(site.id)}>Yes, delete</button>
                  <button class="btn btn--sm" type="button" onclick={() => (confirmDelete = null)}>No</button>
                </div>
              </div>
            {/if}

            {#if editing === site.id}
              <div class="editform">
                <SiteFields bind:site={draft} {fieldErrors} />
                {#if error}<p class="field__flag">{error}</p>{/if}
                {#if confirmRename === site.id}
                  <div class="ask">
                    <p>Renaming rewrites the job name on {linkedFormCount(site)} linked forms (printed sheets included). Rename?</p>
                    <div class="ask__btns">
                      <button class="btn btn--primary btn--sm" type="button" onclick={() => void save(site.id)}>Yes, rename</button>
                      <button class="btn btn--sm" type="button" onclick={() => (confirmRename = null)}>No</button>
                    </div>
                  </div>
                {:else}
                  <div class="editform__btns">
                    <button class="a-cta" type="button" disabled={saving} onclick={() => requestSave(site)}>Save</button>
                    <button class="btn" type="button" onclick={closeEdit}>Cancel</button>
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        </div>
      </li>
    {/each}
  </ul>
{/if}

<style>
  .note {
    margin: 0 0 0.9rem;
    font-size: 0.84rem;
    color: var(--ink-soft);
  }

  /* admin-look.css sizes no icon inside a chip, and an unsized SVG defaults
     to 300x150. */
  .a-chip svg {
    width: 0.9rem;
    height: 0.9rem;
    flex: 0 0 auto;
    margin-right: 0.3rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.4;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  /* The card is not a link — the name inside it is — so it must not lift on
     hover and promise a click the card itself will not honour. */
  .site {
    grid-template-columns: auto 1fr;
    align-items: stretch;
  }
  .site:hover {
    transform: none;
    box-shadow: var(--shadow);
  }
  .site:active {
    transform: none;
  }

  .site__body {
    display: grid;
    gap: 0.5rem;
    min-width: 0;
    padding: 0.1rem 0;
  }

  /* The name owns the whole row and the chevron sits at its far edge, as on a
     quality site card — a chevron parked mid-row reads as part of the name. */
  .site__name {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    min-height: var(--tap);
    padding: 0.35rem 0;
    font-size: 1.15rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: inherit;
    text-decoration: none;
  }
  .site__nametext {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .site__name .a-card__chev {
    flex: 0 0 auto;
  }
  .site__name:hover .site__nametext {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .site__name:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
    border-radius: var(--radius);
  }

  .site__dates {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.3rem 1.1rem;
    margin: 0;
    font-size: 0.82rem;
    color: var(--ink-soft);
  }
  .site__dates small {
    font-size: 0.66rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }

  .figs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(6.5rem, 1fr));
    gap: 0.5rem 0.9rem;
    margin: 0;
    padding-top: 0.5rem;
    border-top: 1px solid var(--line-soft);
  }
  .fig dt {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }
  .fig dd {
    margin: 0;
    font-size: 1.02rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
  /* Amber rather than the error red: a loss is a figure the admin came here
     to read, not something that has gone wrong. */
  .fig dd.is-neg {
    color: var(--warn);
  }
  .fig .pill {
    margin-left: 0.3rem;
  }
  .fig__note {
    display: block;
    margin-top: 0.1rem;
    font-size: 0.72rem;
    font-weight: 400;
    color: var(--ink-faint);
  }

  .acts {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  /* Inline banner: this app has no modals. */
  .ask {
    display: grid;
    gap: 0.5rem;
    padding: 0.6rem 0.7rem;
    border-radius: var(--radius);
    border-left: 4px solid var(--brand);
    background: var(--brand-tint);
  }
  .ask--danger {
    border-left-color: var(--hold);
    background: var(--hold-tint);
  }
  .ask p {
    margin: 0;
    font-size: 0.88rem;
    font-weight: 600;
  }
  .ask__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .editform {
    display: grid;
    gap: 0.6rem;
    padding-top: 0.6rem;
    border-top: 1px solid var(--line-soft);
    max-width: 40rem;
  }
  .editform__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .empty {
    display: grid;
    gap: 0.4rem;
    justify-items: start;
    background: var(--paper);
    border-radius: var(--radius-lg);
    padding: 1.2rem;
    box-shadow: var(--shadow);
  }
</style>
